import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Popconfirm,
  Input,
  Select,
  Row,
  Col,
  Card,
  message,
  Typography,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useMenus } from '../../hooks/useMenus';
import type { Menu } from '../../services/menu.service';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;

const POSITION_LABELS: Record<string, string> = {
  main: 'Menu chính',
  footer: 'Footer',
  sidebar: 'Sidebar',
  header: 'Header',
  mobile: 'Mobile',
  top: 'Top',
};

const COLLECTION_LABELS: Record<string, string> = {
  vang: 'Vàng',
  bac: 'Bạc',
};

export const MenuList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  const { menus, loading, meta, filters, setFilters, deleteMenus } = useMenus();

  const handleSearch = (value: string) => {
    setSearchText(value);
    setFilters({ ...filters, q: value, page: 1 });
  };

  const handleStateFilter = (value: string | undefined) => {
    setFilters({ ...filters, state: value !== undefined ? Number(value) : undefined, page: 1 });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMenus([id]);
      message.success('Xóa menu thành công');
    } catch {
      message.error('Xóa menu thất bại');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      await deleteMenus(selectedRowKeys);
      setSelectedRowKeys([]);
      message.success(`Đã xóa ${selectedRowKeys.length} menu`);
    } catch {
      message.error('Xóa menu thất bại');
    }
  };

  const columns: ColumnsType<Menu> = [
    {
      title: 'Tên menu',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Menu) => (
        <Button type="link" onClick={() => navigate(`/menu/${record._id}/edit`)} style={{ padding: 0 }}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => <code style={{ fontSize: 12 }}>{slug}</code>,
    },
    {
      title: 'Vị trí',
      dataIndex: 'position',
      key: 'position',
      render: (positions: string[]) => (
        <Space wrap>
          {(positions || []).map((pos) => (
            <Tag key={pos} color="blue">
              {POSITION_LABELS[pos] || pos}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Collection',
      dataIndex: 'collection_slug',
      key: 'collection_slug',
      render: (val: string | null) =>
        val ? (
          <Tag color={val === 'vang' ? 'gold' : 'default'}>{COLLECTION_LABELS[val] || val}</Tag>
        ) : (
          <Tag>Tất cả</Tag>
        ),
      width: 110,
    },
    {
      title: 'Số items',
      key: 'items_count',
      render: (_: any, record: Menu) => (
        <Badge count={record.items?.length || 0} showZero style={{ backgroundColor: '#52c41a' }} />
      ),
      width: 100,
      align: 'center',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      key: 'state',
      render: (state: number) => (
        <Tag color={state === 1 ? 'success' : 'error'}>{state === 1 ? 'Hoạt động' : 'Tắt'}</Tag>
      ),
      width: 120,
      align: 'center',
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      width: 160,
      sorter: true,
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_: any, record: Menu) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/menu/${record._id}/edit`)}
            title="Chỉnh sửa"
          />
          <Popconfirm
            title="Xóa menu này?"
            description="Thao tác này không thể hoàn tác."
            onConfirm={() => handleDelete(record._id)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} title="Xóa" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Quản lý Menu</Title>
          <p style={{ color: '#8c8c8c', margin: '4px 0 0' }}>Quản lý các menu điều hướng của website</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/menu/add')}
        >
          Thêm menu
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col flex={1}>
            <Search
              placeholder="Tìm kiếm theo tên menu..."
              allowClear
              onSearch={handleSearch}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Select
              placeholder="Trạng thái"
              allowClear
              style={{ width: 150 }}
              onChange={handleStateFilter}
            >
              <Option value="1">Hoạt động</Option>
              <Option value="0">Tắt</Option>
            </Select>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => setFilters({ page: 1, limit: 10 })}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Popconfirm
            title={`Xóa ${selectedRowKeys.length} menu đã chọn?`}
            onConfirm={handleDeleteSelected}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Xóa {selectedRowKeys.length} menu đã chọn
            </Button>
          </Popconfirm>
        </div>
      )}

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={menus}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          showSizeChanger: true,
          showTotal: (total) => `Tổng ${total} menu`,
          onChange: (page, pageSize) => setFilters({ ...filters, page, limit: pageSize }),
        }}
        onChange={(_, __, sorter: any) => {
          if (sorter.field) {
            setFilters({
              ...filters,
              sort: sorter.field,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            });
          }
        }}
      />
    </div>
  );
};

export default MenuList;
